import React from 'react';
import {
  IInternalRegulationDocumentInstance,
  IInternalRegulationDocumentReviewStatus,
} from '../../../graphql/generated';
import { useRegulation } from '../../RegulationProvider';

export const calculateAllDocumentsStatus = (
  documents: IInternalRegulationDocumentInstance[],
  statusField: 'obligationReviewStatus' | 'mappingsReviewStatus' = 'obligationReviewStatus',
): IInternalRegulationDocumentReviewStatus => {
  if (documents.length === 0) return IInternalRegulationDocumentReviewStatus.Reviewed;

  const getStatus = (doc: IInternalRegulationDocumentInstance) =>
    statusField === 'mappingsReviewStatus' ? doc.mappingsReviewStatus : doc.obligationReviewStatus;

  if (documents.every((doc) => getStatus(doc) === IInternalRegulationDocumentReviewStatus.Reviewed)) {
    return IInternalRegulationDocumentReviewStatus.Reviewed;
  }

  if (documents.every((doc) => getStatus(doc) === IInternalRegulationDocumentReviewStatus.ToReview)) {
    return IInternalRegulationDocumentReviewStatus.ToReview;
  }

  return IInternalRegulationDocumentReviewStatus.InProgress;
};

export const useAllDocuments = (
  documents: IInternalRegulationDocumentInstance[],
  initialDocuments: IInternalRegulationDocumentInstance[],
  offerAllDocuments?: boolean,
  statusField: 'obligationReviewStatus' | 'mappingsReviewStatus' = 'obligationReviewStatus',
): IInternalRegulationDocumentInstance[] => {
  const { activeRegulationTabId } = useRegulation();

  return React.useMemo(() => {
    if (!offerAllDocuments || !documents?.length) return documents;

    const allDocumentsStatus = calculateAllDocumentsStatus(initialDocuments, statusField);

    const allDocs: IInternalRegulationDocumentInstance = {
      id: undefined!,
      __typename: 'InternalRegulationDocumentInstance',
      name: 'All Documents',
      obligationReviewStatus: allDocumentsStatus,
      mappingsReviewStatus: allDocumentsStatus,
      createdTimestamp: '',
      regulationDocumentId: '',
      documentDisplayCode: 'All Documents',
      documentCode: 'All Documents',
    };

    return [allDocs, ...documents];
  }, [documents, initialDocuments, offerAllDocuments, activeRegulationTabId, statusField]);
};
